import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
const toast = { success: (m) => console.log("[toast]", m), error: (m) => console.error("[toast]", m), info: (m) => console.log("[toast]", m) };
import PageContainer, { PageHeader } from "@/components/PageContainer";
import api from "@/lib/assessments";
import { readAll } from "@/lib/assessments";
const formatApiError = (e) => (e && e.message) || "Unknown error";
import { Save, ChevronLeft } from "lucide-react";
import { CHIME_DOMAINS, CHIME_ITEM_SCALE, interpretCHIME } from "@/data/instruments";

export default function CHIME() {
  const navigate = useNavigate();
  const [patient, setPatient] = useState("");
  const [notes, setNotes] = useState("");
  const [values, setValues] = useState({}); // key: `${domain}-${idx}` => 0/1/2
  const [saving, setSaving] = useState(false);

  const domainTotals = {};
  CHIME_DOMAINS.forEach((d) => {
    domainTotals[d.id] = d.items.reduce(
      (a, _, i) => a + (values[`${d.id}-${i}`] || 0),
      0,
    );
  });
  const totalScore = Object.values(domainTotals).reduce((a, b) => a + b, 0);
  const complete = CHIME_DOMAINS.every((d) => d.items.every((_, i) => values[`${d.id}-${i}`] !== undefined));
  const interp = complete ? interpretCHIME(domainTotals) : null;

  const save = async () => {
    if (!complete) {
      toast.error("Please score every item across all five CHIME domains.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/assessments", {
        kind: "chime",
        patient_identifier: patient || null,
        responses: { values, domain_totals: domainTotals },
        score: totalScore,
        interpretation: interp.band,
        notes: notes || null,
      });
      toast.success("CHIME audit saved to history.");
      navigate("/portal/continuum/tools/history");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageContainer testid="chime-page">
      <Link to="/portal/continuum/tools" className="text-sm text-ink-muted hover:text-navy inline-flex items-center gap-1 mb-4" data-testid="chime-back">
        <ChevronLeft size={14} strokeWidth={2} /> All tools
      </Link>

      <PageHeader overline="Portal · Tool" title="CHIME Domain Audit"
        subtitle="Audit progress across the five CHIME recovery domains for one patient. Each item is scored Absent / Emerging / Established." />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="card p-5">
            <label className="label">Patient identifier</label>
            <input className="field" value={patient} onChange={(e) => setPatient(e.target.value)} placeholder="Initials or MRN" data-testid="chime-patient" />
          </div>

          {CHIME_DOMAINS.map((d) => (
            <div key={d.id} className="card p-5" data-testid={`chime-domain-${d.id}`}>
              <div className="flex items-baseline justify-between mb-3">
                <h3 className="text-navy font-semibold">{d.label}</h3>
                <span className="text-sm text-teal font-medium">{domainTotals[d.id]} / {d.items.length * 2}</span>
              </div>
              <ol className="space-y-4">
                {d.items.map((text, i) => (
                  <li key={i} className="border-b border-line-soft pb-4 last:border-0 last:pb-0" data-testid={`chime-item-${d.id}-${i}`}>
                    <div className="text-sm text-ink mb-2">{text}</div>
                    <div className="flex flex-wrap gap-2">
                      {CHIME_ITEM_SCALE.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setValues({ ...values, [`${d.id}-${i}`]: opt.value })}
                          className={`btn text-sm ${values[`${d.id}-${i}`] === opt.value ? "btn-primary" : "btn-secondary"}`}
                          data-testid={`chime-item-${d.id}-${i}-opt-${opt.value}`}
                        >
                          <span className="text-xs text-ink-faint mr-1">{opt.value}</span>{opt.label}
                        </button>
                      ))}
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          ))}

          <div className="card p-5">
            <label className="label">Clinician notes</label>
            <textarea className="field min-h-[80px]" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="chime-notes" />
          </div>
        </div>

        <aside className="lg:sticky lg:top-6 self-start">
          <div className="card p-6" data-testid="chime-summary">
            <div className="overline mb-2">Overall</div>
            <div className="text-5xl font-semibold text-navy">{totalScore}<span className="text-xl text-ink-muted"> / 40</span></div>
            {interp ? (
              <div className="mt-4 fade-up">
                <div className="badge badge-teal mb-2">{interp.band}</div>
                <p className="text-sm text-ink leading-relaxed">{interp.detail}</p>
              </div>
            ) : (
              <div className="mt-4 text-sm text-ink-muted">Score every item to see the band.</div>
            )}

            <div className="rule my-4" />
            <div className="text-xs uppercase tracking-wider text-ink-muted mb-2">Domain scores</div>
            <ul className="text-sm space-y-1">
              {CHIME_DOMAINS.map((d) => (
                <li key={d.id} className="flex items-center justify-between text-ink">
                  <span>{d.label}</span>
                  <span className="text-navy font-medium">{domainTotals[d.id]} / 8</span>
                </li>
              ))}
            </ul>

            <div className="rule my-4" />
            <button onClick={save} disabled={!complete || saving} className="btn btn-primary w-full justify-center" data-testid="chime-save">
              {saving ? "Saving..." : (<><Save size={14} strokeWidth={2} /> Save to history</>)}
            </button>
          </div>
        </aside>
      </div>
    </PageContainer>
  );
}
